import React, { useState, useEffect } from "react";
import { createLeave, updateLeave } from "../../services/LeaveService.js";
import { getAllCourses } from "../../services/CourseService.js";
import { CheckCircle } from "lucide-react";
import bannerImg from "../../images/bannar.png";
import logoImg from "../../images/logo.png";
import { FormField, Input, Textarea, FileInput, Select, SignatureInput } from "../SharedInputs";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { fileStorageService  } from "../../services/FileStorageService";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import ImageModule from "docxtemplater-image-module-free";

function LeaveForm({ onCancel, initialData, isPublic = false, showSubmit = false }) {
  const [formData, setFormData] = useState({
    studentName: "",
    email: "",
    studentId: "",
    courseEnrolled: "",
    reason: "",
    startDate: "",
    endDate: "",
    totalDays: 0,
    signature: "",
    signatureDate: "",
    authorizedName: "",
    authorizedPosition: "",
    authorizedSignature: "",
    authorizedDate: "",
    status: "pending",
    comments: "",
    supportingFile: null,
  });

  const [submitted, setSubmitted] = useState(false);
  const [courses, setCourses] = useState([]);
  const [errors, setErrors] = useState({});
  const [fileError, setFileError] = useState("");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCourses = async () => {
        try {
          const data = await getAllCourses();
          const filteredCourses = data.filter(
            (c) => c.status.toLowerCase() === "active" && !c.courseName.toLowerCase().includes("test")
          );
          setCourses(filteredCourses);
        } catch (error) {
          alert("Failed to load courses");
        }
    };

    fetchCourses();
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser); // null if not logged in
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (initialData) setFormData(prev => ({ ...prev, ...initialData }));
  }, [initialData]);

  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      const diffDays = Math.max(Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1, 0);
      if (diffDays !== formData.totalDays) setFormData(prev => ({ ...prev, totalDays: diffDays }));
    }
  }, [formData.startDate, formData.endDate]);

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: "" })); // clear error on change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    let valid = true;
    let tempErrors = {};

    // Mandatory checks
    if (!formData.studentId) { tempErrors.studentId = "Student Id is required"; valid = false; }
    if (!formData.studentName) { tempErrors.studentName = "Student Name is required"; valid = false; }
    if (!formData.email) { tempErrors.email = "Student Email is required"; valid = false; }
    if (!formData.courseEnrolled) { tempErrors.courseEnrolled = "Course information is required"; valid = false; }
    if (!formData.reason) { tempErrors.reason = "Reason for leave is required"; valid = false; }
    if (!formData.startDate) { tempErrors.startDate = "Start date is required"; valid = false; }
    if (!formData.endDate) { tempErrors.endDate = "End date is required"; valid = false; }
    if (formData.startDate && isPublic) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const start = new Date(formData.startDate);

      if (start < today) {
        tempErrors.startDate = "Start date cannot be in the past";
        valid = false;
      }
    }

    if (formData.endDate && isPublic) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const end = new Date(formData.endDate);

      if (end < today) {
        tempErrors.endDate = "End date cannot be in the past";
        valid = false;
      }
    }
    if (!formData.totalDays) { tempErrors.signature = "Invalid number of days"; valid = false; }
    if (!formData.signature) { tempErrors.signature = "Student signature is required"; valid = false; }
    if (!formData.supportingFile) { setFileError("Supporting document is required"); valid = false; }

    // Mandatory Office Use fields (only if user is logged in)
    if (!isPublic) {
      if (!formData.status) tempErrors.status = "Status is required";
      if (!formData.authorizedName) tempErrors.authorizedName = "Authorized Name is required";
      if (!formData.authorizedPosition) tempErrors.authorizedPosition = "Position is required";
      if (!formData.authorizedSignature) tempErrors.authorizedSignature = "Authorized Signature is required";

      // If any authorized field is missing → invalidate
      if (Object.keys(tempErrors).some((key) => key.startsWith("authorized") || key === "status" || key === "comments")) {
        valid = false;
      }
    }

    setErrors(tempErrors);
    if (!valid) {
      setLoading(false);
      return;
    }

    try {
      let dataToSave = { ...formData };

      if (initialData?.id) {
        dataToSave.leaveApplicationFile = (isPublic && !formData.authorizedSignature) ? {} : await handleLeaveDocx();
        await updateLeave(initialData.id, dataToSave);
        // 6. Open uploaded file in a new tab
        window.open(dataToSave.leaveApplicationFile.url, "_blank");
      } else {
        // Upload supporting file if exists
        if (formData.supportingFile) {
          const uploadedFiles = await fileStorageService.uploadFiles(
            [formData.supportingFile], // support multiple if needed
            `leave/${formData.studentId}/${formData.signatureDate}/supportingFiles`
          );
          dataToSave.supportingFile = uploadedFiles.map((f, i) => ({
            url: f.url,
            path: f.path,
            name: formData.supportingFile[i]?.name || formData.supportingFile.name
          }))[0]; // single file
        }
        await createLeave(dataToSave);
      }
      setSubmitted(true);
    } catch (err) {
      alert("Failed to submit leave request");
    } finally {
      setLoading(false);
    }
  };
  const processDocxTemplate = (arrayBuffer, mapping) => {
    const zip = new PizZip(arrayBuffer);
    
    // Image module configuration
    const imageOpts = {
      centered: false, // Set to true if you want centered images
      getImage: (tagValue) => {
        // Convert base64 to array buffer
        if (tagValue && tagValue.startsWith('data:image')) {
          // Remove data:image/png;base64, prefix
          const base64Data = tagValue.split(',')[1];
          const binaryString = atob(base64Data);
          const len = binaryString.length;
          const bytes = new Uint8Array(len);
          for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          return bytes;
        }
        // If just base64 without prefix
        const binaryString = atob(tagValue);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      },
      getSize: (img, tagValue, tagName) => {
        return [250, 100]; // Default size
      }
    };

    const doc = new Docxtemplater(zip, {
      paragraphLoop: true,
      linebreaks: true,
      delimiters: { start: "[[", end: "]]" },
      modules: [new ImageModule(imageOpts)]
    });

    try {
      doc.render(mapping);
    } catch (error) {
      throw error;
    }

    const out = doc.getZip().generate({ type: "blob" });
    return out;
  };

  
    // Mapping function to convert your data structure to template placeholders
    const createTemplateMapping = (data) => {
      return {
        // Personal Details
        'studentId': data.studentId || '',
        'studentEmail': data.email || '',
        'studentName': data.studentName || '',
        'courseName': data.courseEnrolled || '',
        'reasonForLeave': data.reason || '',
        'firstDate': data.startDate || '',
        'returnDate': data.endDate || '',
        'totalNumberOfDays': data.totalDays || '',
        'studentSignature': data.signature || '',
        'studentSignDate': data.signatureDate ? new Date(data.signatureDate).toLocaleDateString() : '',

        // Office Use Only
        'officeApproved': data.status === 'approved' ? '☑' : '☐',
        'officeNotApproved': data.status === 'not approved' ? '☑' : '☐',
        'officeComments': data.comments || '',
        'officeAuthName': data.authorizedName || '',
        'officePositionName': data.authorizedPosition || '',
        'officeAuthSign': data.authorizedSignature || '',
        'officeAuthSignDate': data.authorizedDate ? new Date(data.authorizedDate).toLocaleDateString() : new Date().toLocaleDateString(),
      };
    };
    // Format a date object as YYYY-MM-DD
    const formatDateWithDash = (date) => {
      if (!date) return "";
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

   
    // Generate Enrolment Form → DOCX
    const handleLeaveDocx = async () => {
      if (!initialData?.id) return;
  
      try {
        // 1. Fetch template
        const templatePath = "/templates/Leave Application.docx";
        const response = await fetch(templatePath);
        if (!response.ok) throw new Error("Template not found");
  
        const arrayBuffer = await response.arrayBuffer();
        const templateMapping = createTemplateMapping(formData);
  
        // 2. Fill DOCX template
        const filledDoc = processDocxTemplate(arrayBuffer, templateMapping);
  
        const filePath = `leave/applicationForms/${formData.studentId}/${formData.studentId + " for date " + formatDateWithDash(formData.signatureDate)}.docx`;
        // 3. Check if file exists
        let fileUrl;
        try {
          fileUrl = await fileStorageService.getFileUrl(filePath);
        } catch (err) {
        }
  
        // 4. Upload (this will overwrite if file exists)
        const fileData = await fileStorageService.uploadFile(filledDoc, filePath);
        
  
        // 5. Update enrollment record
        return {
            url: fileData.url,
            path: fileData.path,
            name: `leave-${formData.studentId}-${formatDateWithDash(formData.signatureDate)}.docx`
          };
  
      } catch (err) {
        alert("Failed to generate leave application docx");
      }
      return null;
    };



  // ... Submitted view logic unchanged
  if (submitted && isPublic) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100vh", padding: "2rem 1rem", background: "#f5f5f5" }}>
        <img src={bannerImg} alt="Banner" style={{ width: "100%", maxWidth: 700, maxHeight: 200, objectFit: "contain", marginBottom: 16 }} />
        <div style={{ maxWidth: 600, padding: "2rem", background: "#fff", borderRadius: 12, textAlign: "center", boxShadow: "0 4px 10px rgba(0,0,0,0.1)" }}>
          <CheckCircle size={64} color="#10b981" style={{ marginBottom: 16 }} />
          <h1 style={{marginBottom: "1rem"}}>Leave Request Submitted Successfully!</h1>
          <p style={{marginBottom: "1rem"}}>Thank you for your submission. <strong>CIET Admin Team</strong> will review and notify you about your application request.</p>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "1rem"}}>
            <button className="btn btn-primary" onClick={() => window.location.href="/leave-application"} style={{marginBottom: "1rem", alignItems: "center"}}>
              Submit Another Request
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (submitted && !isPublic) { onCancel(); return null; }

  // Render form
  return (
    <div className="form-container"
        style={{
        maxWidth: isPublic ? "700px" : "100%",
        margin: isPublic ? "3rem auto" : "0",
        padding: isPublic ? "3rem 2rem" : "2rem",
        borderRadius: isPublic ? "12px" : "0",
        boxShadow: isPublic ? "0 4px 12px rgba(0,0,0,0.1)" : "none",
        background: isPublic ? "#fff" : "transparent",
        position: "relative"
    }}>
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
            borderRadius: isPublic ? "12px" : "0"
          }}
        >
          <div className="spinner" style={{ textAlign: "center" }}>
            <div className="loader" style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 12px",
              
            }} />
            
          </div>
          <p style={ {
            padding: "0.5rem",
          }
          }>Submitting, please wait!</p>
        </div>
      )}
      <form onSubmit={handleSubmit}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            marginBottom: 24,
          }}
        >
          {isPublic ? (
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              <img src={logoImg} alt="Logo" style={{ height: 64 }} />
              <h1>Leave Application</h1>
            </div>
          ) : (
            <div>
              <h1 className="page-title">Leave Application</h1>
              {isPublic && <p className="page-subtitle">Submit a new leave request</p>}
            </div>
          )}

          {!isPublic && (
            <div style={{ display: "flex", gap: 16 }}>
              <button type="button" className="btn btn-secondary" onClick={onCancel}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                  {formData?.leaveApplicationFile?.url ? "Save & Re-generate Leave Form" : "Save & Generate Leave Form"}
              </button>
              {/* Show View Application Form button if file exists */}
              {formData.leaveApplicationFile?.url && (
                <a
                  href={formData.leaveApplicationFile.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  View Application Form
                </a>
              ) }
            </div>
          )}
        </div>


        {/* Student Info */}
        <div className="form-section" style={{ marginBottom: 24 }}>
          <h2>Student Information</h2><hr style={{ margin: "8px 0 16px" }} />
          <div className="form-grid" style={{ display: "grid", gap: 16 }}>
            <FormField label="Student Id" required error={errors.studentId}>
              <Input name="studentId" value={formData.studentId} onChange={e => handleChange("studentId", e.target.value)} />
            </FormField>
            <FormField label="Student Name" required error={errors.studentName}>
              <Input name="studentName" value={formData.studentName} onChange={e => handleChange("studentName", e.target.value)} />
            </FormField>
            <FormField label="Student Email" required error={errors.email}>
              <Input name="email" type="email" value={formData.email} onChange={e => handleChange("email", e.target.value)} />
            </FormField>
            <FormField label="Course Enrolled" required error={errors.courseEnrolled}>
              <Select
                name="courseEnrolled"
                value={formData.courseEnrolled}
                onChange={e => handleChange("courseEnrolled", e.target.value)}
              >
                <option value="">Select a course</option>
                {courses.map(course => (
                  <option key={course.courseCode} value={course.courseName}>
                    {course.courseName}
                  </option>
                ))}
              </Select>
            </FormField>

          </div>
        </div>

        {/* Leave Details */}
        <div className="form-section" style={{ marginBottom: 24 }}>
          <h2>Leave Details</h2><hr style={{ margin: "8px 0 16px" }} />
          <div className="form-grid" style={{ display: "grid", gap: 16 }}>
            <FormField label="Reason for Leave" required error={errors.reason}>
              <Textarea name="reason" value={formData.reason} onChange={e => handleChange("reason", e.target.value)} />
            </FormField>
            <FormField label="First Day of Leave" required error={errors.startDate}>
              <Input type="date" name="startDate" value={formData.startDate} min={isPublic ? new Date().toISOString().split("T")[0] : {}} onChange={e => handleChange("startDate", e.target.value)} />
            </FormField>
            <FormField label="Return Date" required error={errors.endDate}>
              <Input type="date" name="endDate" value={formData.endDate}  min={isPublic ? new Date().toISOString().split("T")[0] : {}} onChange={e => handleChange("endDate", e.target.value)} />
            </FormField>
            <FormField label="Total Number of Days" error={errors.totalDays}>
              <Input type="number" name="totalDays" value={formData.totalDays} readOnly />
            </FormField>
            <FormField label="Supporting Document" required error={fileError} hint="Upload supporting documents">
              <FileInput accept=".pdf,.jpg,.jpeg,.png"
              maxSize={2 * 1024 * 1024} // 2 MB
              value={formData.supportingFile} 
              onChange={file => handleChange("supportingFile", file)}
              placeholder="Upload a supporting document for the leave"
              required
              error={errors.leaveApplicationFile} />
            </FormField>
          </div>
        </div>

        {/* Student Signature */}
        <div className="form-section" style={{ marginBottom: 24 }}>
          <h2>Student Signature</h2><hr style={{ margin: "8px 0 16px" }} />
          <div className="form-grid" style={{ display: "grid", gap: 16 }}>
            <FormField label="Student Name">
              <Input value={formData.studentName} readOnly />
            </FormField>
            <FormField label="Signature" required error={errors.signature}>
              <SignatureInput
                value={formData.signature}
                onChange={(val) => handleChange("signature", val)}
                readOnly={!isPublic && user}
              />
            </FormField>
            {!isPublic && user &&
              <FormField label="Date" required error={errors.signatureDate} readOnly>
                <Input name="signatureDate" value={new Date(formData.signatureDate).toLocaleDateString()} readOnly/>
              </FormField>
            }
          </div>
        </div>

        {/* Office Use */} 
        {user && !isPublic && (
        <div className="form-section" style={{ marginBottom: 24 }}> 
          <h2>Office Use Only</h2>
          <hr style={{ margin: "8px 0 16px" }} /> 
          <div className="form-grid" style={{ display: "grid", gap: 16 }}>
            <FormField label="Status" required error={errors.status}>
              <div style={{ display: 'flex', gap: 16 }}>
                <label>
                  <input
                    type="radio"
                    name="status"
                    value="approved"
                    checked={formData.status === "approved"}
                    onChange={e => handleChange("status", e.target.value)}
                  />{" "}
                  Approved
                </label>
                <label>
                  <input
                    type="radio"
                    name="status"
                    value="not approved"
                    checked={formData.status === "not approved"}
                    onChange={e => handleChange("status", e.target.value)}
                  />{" "}
                  Not Approved
                </label>
              </div>
            </FormField>

            <FormField label="Comments" error={errors.comments}>
              <Textarea
                name="comments"
                value={formData.comments}
                onChange={e => handleChange("comments", e.target.value)}
              />
            </FormField>

            <FormField label="Authorised Person’s Name" required error={errors.authorizedName}>
              <Input
                name="authorizedName"
                value={formData.authorizedName}
                onChange={e => handleChange("authorizedName", e.target.value)}
              />
            </FormField>

            <FormField label="Position" required error={errors.authorizedPosition}>
              <Input
                name="authorizedPosition"
                value={formData.authorizedPosition}
                onChange={e => handleChange("authorizedPosition", e.target.value)}
              />
            </FormField>

            <FormField 
              label="Authorized Signature" 
              required 
              error={errors.authorizedSignature}
            >
              <SignatureInput
                value={formData.authorizedSignature}
                onChange={(val) => handleChange("authorizedSignature", val)}
              />
            </FormField>
          </div>
 
        </div>
        )}

        {isPublic && <div style={{ display: "flex", justifyContent: "center", gap: 16 }}><button type="submit" className="btn btn-primary">Submit Leave</button></div>}
      </form>
    </div>
  );
}

export default LeaveForm;
